/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/popup.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Rendering } from 'components/rendering'
import { Highlighter } from 'components/highlighter'
import { SmallMultiples } from 'components/smallmultiples'
import { Methodology } from 'components/methodology'
import { Link } from '@ta-interaktiv/contentbricks'
import { Tooltip } from 'react-tippy'
import 'react-tippy/dist/tippy.css'
import renderHTML from 'react-render-html'
import AnchorLink from 'react-anchor-link-smooth-scroll'

const ARTICLE_ID = '609432742690'

const optimisticText =
  "<p>Le scénario optimiste <span class='ui green label'>SSP1-1.9</span> décrit un monde dans lequel les émissions mondiales de CO<sub>2</sub> sont réduites à zéro net d'ici 2050 environ. Cela permettrait d'atteindre l'objectif de l'accord de Paris, à savoir limiter le réchauffement global causé par l'homme à 1,5 degré Celsius au-dessus des températures préindustrielles.</p>"

const pessimisticText =
  "<p>Le scénario pessimiste <span class='ui red label'>SSP5-8.5</span> montre un avenir de croissance économique alimentée par la combustion de pétrole, de gaz et de charbon, avec un réchauffement mondial prévu de 4,4 degrés Celsius d'ici la fin du siècle.</p>"

const labelText = ['Avec protection', 'Sans protection']

export class AppComponent extends React.Component<Props, State> {
  constructor() {
    super()
    this.state = {
      isMobile: window.innerWidth < 600
    }
    this.updateDimensions = this.updateDimensions.bind(this)
  }

  updateDimensions() {
    // console.log('updateDimensions')
    this.setState({ isMobile: window.innerWidth < 600 })
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions)
  }

  render() {
    return (
      <div className='index inverted'>
        <Masthead inverted articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical  very fitted segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1>Le recul des glaciers comme vous ne l’avez jamais vu</h1>
              <h2>
                Des simulations de l'Université de Fribourg et de l'EPFZ
                montrent l’ampleur du recul de cinq glaciers suisses. Découvrez
                ce qu’on peut encore sauver grâce à la protection du climat.
              </h2>
            </div>
          </div>
        </header>

        <div className='ui vertical segment' style={{ padding: '0 0 2em' }}>
          <div className='ui container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                Matthias Huss, Enrico Mattea, Mathias Lutz, Marc Brupbacher
              </div>
              <div className='ta updated'>Mis à jour le 7 juillet 2022</div>
              <div
  style={{
    fontFamily: 'TamediaUI',
    fontSize: '.7rem',
    lineHeight: '1.4rem',
    color: '#7d7d7d',
    fontStyle: 'italic',
    marginTop: '1em'
  }}
>
Durant les fêtes, nous republions un certains nombre d'articles qui vous ont spécialement plu.
</div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment'>
          <Rendering
            title='Glacier du Rhône'
            canton='VS'
            scenario={labelText}
            showHint
            mp4={require('../../videos/rhone-119-1280-hevc.mp4')}
            webm={require('../../videos/rhone-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/rhone-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/rhone-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/rhone-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/rhone-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/rhone-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/rhone-585-1920-vp9.webm')}
            posterImage={require('../../images/rhone-1280.png')}
            posterImageDesktop={require('../../images/rhone-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              En 1884, les premières mesures à long terme du monde ont été
              effectuées sur le glacier du Rhône pour déterminer la fonte des
              glaciers. Le glacier est bien exploité par les touristes et
              constitue la source du Rhône, l'un des plus grands fleuves de
              Suisse. Vers 2007, un lac s'est formé à l'extrémité du glacier, et
              depuis, il grandit rapidement d’année en année.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    Avec protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/rhone-2100-ssp119.png')}
                  overlayImage={require('../../images/rhone-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    Sans protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/rhone-2100-ssp585.png')}
                  overlayImage={require('../../images/rhone-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Entre 2030 et 2040, la langue du glacier du Rhône devrait perdre
                le contact avec le lac, comme le montrent des simulations de
                l'EPFZ et de l’Université de Fribourg (
                <AnchorLink href='#methodik' className='nav-link'>
                  lire la méthodologie
                </AnchorLink>
                ). D'ici là, la grotte glaciaire artificielle, encore
                fréquemment visitée aujourd'hui et dont l'existence est
                prolongée par une toison blanche, ne pourra probablement plus
                être exploitée. Dans un scénario de forte augmentation des
                températures, tous les petits glaciers situés sur les flancs
                autour du glacier du Rhône disparaîtront d'ici 2050, et sa
                langue ne sera plus visible depuis la grotte de glace actuelle.
                Plusieurs lacs glaciaires situés plus haut pourraient alors
                marquer le paysage sans glace. Après 2080, même la tache de
                glace sous le Dammastock ne pourra probablement plus résister à
                la chaleur de l'été. En revanche, en cas de forte protection du
                climat, nous verrons encore un glacier intact en 2100. Or même
                dans ce cas, environ deux tiers du volume actuel seront perdus.
              </div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment dark'>
          <Rendering
            title='Glacier Morteratsch'
            canton='GR'
            scenario={labelText}
            mp4={require('../../videos/morteratsch-119-1280-hevc.mp4')}
            webm={require('../../videos/morteratsch-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/morteratsch-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/morteratsch-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/morteratsch-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/morteratsch-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/morteratsch-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/morteratsch-585-1920-vp9.webm')}
            posterImage={require('../../images/morteratsch-1280.png')}
            posterImageDesktop={require('../../images/morteratsch-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Le glacier Morteratsch, en Engadine, est probablement le seul
              glacier de Suisse dont la langue était accessible en fauteuil
              roulant depuis la gare. Mais le récent retrait de la glace a
              changé la donne. La randonnée de 3 km à travers une vallée de plus
              en plus verte, le long de panneaux qui documentent le recul de la
              glace, est impressionnante.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    Avec protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/morteratsch-2100-ssp119.png')}
                  overlayImage={require('../../images/morteratsch-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    Sans protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/morteratsch-2100-ssp585.png')}
                  overlayImage={require('../../images/morteratsch-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Le premier changement radical dans le système glaciaire s'est
                produit il y a quelques années déjà: le plus grand affluent, le
                Vadret Pers, s'est séparé du courant principal et se retire dans
                une vallée latérale. Bien qu'au cours des prochaines décennies,
                beaucoup de glace continuera à s'écouler à travers le labyrinthe
                de crevasses du «Buuch» du Piz Bernina vers la langue du
                glacier, celle-ci s'amincit rapidement et finira, quel que soit
                le scénario, par perdre son lien avec la zone nourricière. Dans
                le meilleur des cas, le glacier pourra se stabiliser après 2080.
                Mais sans protection climatique, presque toute la glace sera
                perdue. Des lacs pittoresques pourraient également se former à
                l'avenir dans la vallée du glacier de Morteratsch.
              </div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment'>
          <Rendering
            title="Grand glacier d'Aletsch"
            canton='VS'
            scenario={labelText}
            mp4={require('../../videos/aletsch-119-1280-hevc.mp4')}
            webm={require('../../videos/aletsch-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/aletsch-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/aletsch-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/aletsch-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/aletsch-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/aletsch-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/aletsch-585-1920-vp9.webm')}
            posterImage={require('../../images/aletsch-1280.png')}
            posterImageDesktop={require('../../images/aletsch-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Avec une superficie de près de 80 km<sup>2</sup>, le Grand glacier
              d'Aletsch est de loin le plus grand glacier des Alpes. Sa langue
              glaciaire de 14 km de long, avec ses deux moraines centrales, est
              connue dans le monde entier. Le glacier d'Aletsch stocke à lui
              seul près d'un cinquième du volume de glace de la Suisse et, avec
              environ 800 mètres, la glace n'est nulle part plus épaisse qu’à la
              place Concordia, où trois grands glaciers se rejoignent.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    Avec protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/aletsch-2100-ssp119.png')}
                  overlayImage={require('../../images/aletsch-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    Sans protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/aletsch-2100-ssp585.png')}
                  overlayImage={require('../../images/aletsch-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                En raison de sa taille, on pourrait supposer que le glacier
                d'Aletsch est celui qui résiste le mieux aux changements
                climatiques. Mais ce n'est que partiellement le cas: le glacier
                est bien trop grand pour le climat actuel et, même en cas de
                forte protection du climat, il se retirera massivement et perdra
                plus de la moitié de son volume d'ici 2100. La langue incurvée
                du glacier se raccourcit et s'amincit rapidement. Jusqu'en 2050,
                ce phénomène se produira presque indépendamment de l'évolution
                du climat. À partir de 2040 environ, un premier lac devrait
                apparaître dans la vallée encaissée sous le glacier d'Aletsch.
                Ce lac tout comme les nombreux autres qui suivront marqueront
                désormais le paysage. Si l'humanité parvient à maîtriser la
                crise climatique, l'extrémité du glacier pourrait se stabiliser
                d'ici 2100, mais seulement à une dizaine de kilomètres en amont
                de sa position actuelle. En revanche, sans protection du climat,
                un immense lac glaciaire pourrait se former vers 2080 à la place
                Conkordia. Dans ce scénario extrême, il ne resterait même plus
                de glace au Jungfraujoch.
              </div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment dark'>
          <Rendering
            title='Glacier du Gorner'
            canton='VS'
            scenario={labelText}
            mp4={require('../../videos/gorner-119-1280-hevc.mp4')}
            webm={require('../../videos/gorner-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/gorner-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/gorner-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/gorner-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/gorner-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/gorner-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/gorner-585-1920-vp9.webm')}
            posterImage={require('../../images/gorner-1280.png')}
            posterImageDesktop={require('../../images/gorner-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Le glacier du Gorner prend sa source dans les plus hauts sommets
              de Suisse et est le deuxième plus grand glacier des Alpes. Le
              glacier se compose de différents affluents qui ont déjà perdu, en
              partie, le lien avec le courant principal au cours des dernières
              années.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    Avec protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/gorner-2100-ssp119.png')}
                  overlayImage={require('../../images/gorner-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    Sans protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/gorner-2100-ssp585.png')}
                  overlayImage={require('../../images/gorner-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Les différences entre une hausse modérée et une forte hausse des
                températures de l'air sont particulièrement marquées pour le
                glacier du Gorner. En raison de sa grande étendue en altitude,
                le glacier pourrait se stabiliser vers la fin du siècle en cas
                de forte protection du climat. Toutefois, même dans ce cas, une
                masse de glace morte se formera sous la cabane du Mont Rose, où
                la glace a encore aujourd'hui une épaisseur de plus de 400
                mètres. Dès les prochaines années, la glace devrait laisser la
                place à un grand lac devant la langue du glacier. En interaction
                avec un barrage artificiel, celui-ci pourrait à l'avenir jouer
                un rôle important dans la stratégie énergétique 2050 de la
                Confédération. Or selon les modélisations, le bassin du lac ne
                sera libre de glace avant 2050 que dans le scénario pessimiste.
                Dans le meilleur des cas, il restera partiellement rempli de
                glace jusqu'à la fin du siècle. Au Mont Rose, même en cas de
                changement climatique extrême, il restera encore un peu de glace
                en 2100.
              </div>
            </div>
          </div>
        </div>

        <div className='glacier ui vertical segment'>
          <Rendering
            title='Glacier de la Silvretta'
            canton='GR'
            scenario={labelText}
            mp4={require('../../videos/silvretta-119-1280-hevc.mp4')}
            webm={require('../../videos/silvretta-119-1280-vp9.webm')}
            mp4Pessimistic={require('../../videos/silvretta-585-1280-hevc.mp4')}
            webmPessimistic={require('../../videos/silvretta-585-1280-vp9.webm')}
            mp4Desktop={require('../../videos/silvretta-119-1920-hevc.mp4')}
            webmDesktop={require('../../videos/silvretta-119-1920-vp9.webm')}
            mp4DesktopPessimistic={require('../../videos/silvretta-585-1920-hevc.mp4')}
            webmDesktopPessimistic={require('../../videos/silvretta-585-1920-vp9.webm')}
            posterImage={require('../../images/silvretta-1280.png')}
            posterImageDesktop={require('../../images/silvretta-1920.png')}
            isMobile={this.state.isMobile}
          />
          <div className='comparison ui container'>
            <div className='explain ta text'>
              Avec moins de 3 km<sup>2</sup>, le glacier de la Silvretta est le
              plus petit des cinq glaciers sélectionnés. Il est néanmoins très
              important pour l'observation des glaciers suisses, car des mesures
              détaillées de l'accumulation de neige et de la fonte y sont
              effectuées en continu depuis plus de 100 ans.
            </div>
            <div className='two column stackable grid ui equal width'>
              <div className='column'>
                <Tooltip
                  html={renderHTML(optimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label green'>
                    <i className='info circle icon' />
                    Avec protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/silvretta-2100-ssp119.png')}
                  overlayImage={require('../../images/silvretta-2100-ssp119-overlay.png')}
                />
              </div>
              <div className='column'>
                <Tooltip
                  html={renderHTML(pessimisticText)}
                  position='top'
                  trigger='mouseenter'
                  hideOnClick
                  arrow
                >
                  <div className='ui label red'>
                    <i className='info circle icon' />
                    Sans protection du climat
                  </div>
                </Tooltip>
                <h3>Année 2100</h3>
                <Highlighter
                  baseImage={require('../../images/silvretta-2100-ssp585.png')}
                  overlayImage={require('../../images/silvretta-2100-ssp585-overlay.png')}
                />
              </div>
              <div className='explain ta text'>
                Quel que soit le scénario climatique, on peut s'attendre à un
                fort recul de la glace au cours des prochaines décennies. Le
                glacier est déjà relativement mince aujourd'hui, de sorte qu'une
                accélération de la fonte entraînerait rapidement une mise à nu
                du sous-sol. Dans le cas d'un réchauffement modéré, accompagné
                d'une forte protection du climat, le glacier ne perdrait que
                lentement de son épaisseur à partir de 2050. Les glaciologues
                pourraient donc poursuivre cette précieuse série de mesures
                jusqu'à la fin du 21e siècle. Les petits glaciers sont plus
                résistants qu'on ne le pense: si nous parvenons à maîtriser la
                crise climatique, beaucoup pourraient survivre. Il en va tout
                autrement sans protection du climat: la ligne dite d'équilibre,
                c'est-à-dire l'altitude à laquelle la neige peut survivre en
                été, s'élèverait en peu de temps au-dessus du point le plus haut
                du glacier. Il ne lui resterait donc aucune chance de se
                stabiliser, et d'ici 2080 environ, le dernier reste de glace
                devrait avoir disparu. Dans ce cas, il ne resterait plus qu'un
                désert d'éboulis.
              </div>
            </div>
          </div>
        </div>

        <div className='ui vertical segment dark multiple'>
          <div className='ui text container' style={{ margin: '1em 0 2em' }}>
            <div className='ta text'>
              L'évolution du volume de glace a été déterminée par le modèle de
              calcul de l'EPFZ pour chaque glacier suisse et chaque scénario
              climatique. Une comparaison des 38 plus grands glaciers montre des
              points communs, mais aussi des différences dans cette évolution:
            </div>
          </div>
          <div className='ui container' style={{ paddingTop: '1em' }}>
            <div className='ta text'>
              <h4 className='left'>
              Volume des plus grands glaciers suisses jusqu'en 2100
              </h4>
              <div className='Key'>
                <div className='entry'>
                  <div className='swatch'>
                    <svg width='12' height='12'>
                      <line
                        x2='12'
                        y1='12'
                        y2='0'
                        stroke='#2ca29f'
                        strokeWidth='4'
                      />
                    </svg>
                  </div>
                  <div className='label'>{labelText[0]}</div>
                </div>
                <div className='entry'>
                  <div className='swatch'>
                    <svg width='12' height='12'>
                      <line
                        x2='12'
                        y1='12'
                        y2='0'
                        stroke='#d3343f'
                        strokeWidth='4'
                      />
                    </svg>
                  </div>
                  <div className='label'>{labelText[1]}</div>
                </div>
              </div>
              <SmallMultiples isMobile={this.state.isMobile} />
            </div>
          </div>
        </div>

        <div className='ui vertical segment dark'>
          <div className='ui text container'>
            <div className='ta text'>
              À l'exception de très rares glaciers dont l'origine se situe à
              plus de 4000 mètres, toute la glace disparaît dans le pire des
              scénarios. Une telle évolution du climat serait extrêmement
              inconfortable non seulement pour les glaciers, mais aussi pour
              l'ensemble de la population suisse. L'augmentation des
              températures en Suisse serait en effet nettement supérieure aux
              4,4 degrés de la moyenne mondiale, ce qui entraînerait une
              multitude d'autres problèmes comme la chaleur extrême, la
              sécheresse et les fortes pluies. La réaction des glaciers en cas
              de respect de l'objectif de 1,5°C est très différente: alors que
              certains glaciers (Unteraar, Plaine Morte, Forno) disparaîtront
              malgré tout presque entièrement d'ici 2100, d'autres parviendront
              à conserver plus de la moitié de leur volume actuel (Gorner,
              Fiescher, Allalin).
            </div>
          </div>
          <div className='ui ta text teaser container'>
            <div className='column-wrapper'>
              <div className='person'>
                <img
                  className='ui medium circular image left'
                  src={require('../../images/matthias-huss.jpg')}
                />
              </div>
              <div className='quote'>
                «Les glaciers n'ont jamais été aussi mal en point à la fin du
                mois de juin.»
              </div>
            </div>
            <div className='call'>
              Lire{' '}
              <Link href='168155262180' hideIcon allowWordWrap>
                l'interview
              </Link>{' '}
              de Matthias Huss, glaciologue auprès de l’EPFZ.
            </div>
          </div>
        </div>

        <hr className='divider' />

        <Methodology />

        <Footer
          inverted
          locale='fr'
          hidePortfolioTeaser
          mailTo='interactif@tamedia.ch'
          articleId={ARTICLE_ID}
          questions={[
            'Le sujet était-il informatif?',
            'Les visualisations sont-elles compréhensibles?'
          ]}
          credits={[
            [
              'Modélisation des glaciers, Expertise, Texte',
              '<nobr>Matthias Huss</nobr>, Glaciologue, ETH Zürich/WSL Birmensdorf, Directeur du réseau suisse de mesure des glaciers <a href="https://www.glamos.ch">GLAMOS</a>'
            ],
            [
              'Visualisation 3D des glaciers',
              '<nobr>Enrico Mattea</nobr>, Université de Fribourg, Département des géosciences'
            ],
            [
              'Conception et réalisation',
              '<nobr>Mathias Lutz</nobr>, <nobr>Marc Brupbacher</nobr>'
            ]
          ]}
        />
      </div>
    )
  }
}
